import React, { useEffect, useState } from "react";
import axios from "axios";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate } from "react-router-dom";
import { API_URL, Back_url } from "../config";

interface Car {
  id: number;
  name: string;
  model: string;
  type: string;
  price_per_day: number;
  is_available_for_rent: boolean;
  is_in_maintenance: boolean;
  maintenance_days_estimate: number | null;
  photos: { id: number; path: string }[];
}

const colors = {
  primary: "#294b5a",
  primaryHover: "#1a3641",
  text: "#1e293b",
  textMuted: "#5f6b76",
  textFaint: "#94a3b8",
  border: "#dbe0e4",
  borderLight: "#e5e7eb",
  bgPage: "#f4f6f8",
  bgSubtle: "#f8fafc",
  danger: "#ef4444",
  availableBg: "#d1fae5",
  availableText: "#065f46",
  unavailableBg: "#fee2e2",
  unavailableText: "#991b1b",
  maintBg: "#fef3c7",
  maintText: "#92400e",
};

const badgeStyle = (bg: string, text: string): React.CSSProperties => ({
  display: "inline-block",
  padding: "3px 10px",
  borderRadius: 20,
  fontSize: 12,
  fontWeight: 500,
  background: bg,
  color: text,
});

const iconButtonStyle = (danger?: boolean): React.CSSProperties => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: 30,
  height: 30,
  borderRadius: 6,
  border: "1px solid #e2e8f0",
  background: "#ffffff",
  color: danger ? colors.danger : colors.textMuted,
  cursor: "pointer",
  transition: "all 0.15s ease",
});

const togglePillStyle: React.CSSProperties = {
  padding: "4px 10px",
  fontSize: 11,
  fontWeight: 500,
  border: `1px solid ${colors.border}`,
  borderRadius: 20,
  background: "#fff",
  cursor: "pointer",
  whiteSpace: "nowrap",
};

// ── Skeleton Loader ─────────────────────────────────────────────────────────
function SkeletonLoader() {
  return (
    <div style={{ width: "100%", minHeight: "100vh", backgroundColor: "#f4f6f8", padding: "20px 20px", boxSizing: "border-box" }}>
      <style>{`
        @keyframes shimmer {
          0% { background-position: -200% 0; }
          100% { background-position: 200% 0; }
        }
      `}</style>

      {/* Header Skeleton */}
      <div className="d-flex justify-content-between align-items-end mb-4">
        <div>
          <div className="d-flex align-items-center gap-2 mb-1">
            <div style={{
              width: 200,
              height: 28,
              background: "linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)",
              backgroundSize: "200% 100%",
              borderRadius: 6,
              animation: "shimmer 1.5s infinite"
            }}></div>
            <div style={{
              width: 60,
              height: 20,
              background: "linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)",
              backgroundSize: "200% 100%",
              borderRadius: 12,
              animation: "shimmer 1.5s infinite"
            }}></div>
          </div>
          <div style={{
            width: 280,
            height: 18,
            background: "linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)",
            backgroundSize: "200% 100%",
            borderRadius: 4,
            animation: "shimmer 1.5s infinite"
          }}></div>
        </div>
        <div style={{
          width: 150,
          height: 42,
          background: "linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)",
          backgroundSize: "200% 100%",
          borderRadius: 8,
          animation: "shimmer 1.5s infinite"
        }}></div>
      </div>

      {/* Main Card Skeleton */}
      <div style={{
        backgroundColor: "#ffffff",
        borderRadius: "12px",
        border: "1px solid #e2e8f0",
        overflow: "hidden",
        marginTop: "15px",
      }}>
        {/* Filter Bar Skeleton */}
        <div style={{ padding: "16px 20px", borderBottom: "1px solid #edf2f7" }}>
          <div className="row g-3 align-items-center">
            <div className="col-12 col-md-6 col-lg-5">
              <div style={{
                width: "100%",
                height: 42,
                background: "linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)",
                backgroundSize: "200% 100%",
                borderRadius: 8,
                animation: "shimmer 1.5s infinite"
              }}></div>
            </div>
            <div className="col-12 col-md-4 col-lg-3">
              <div style={{
                width: "100%",
                height: 42,
                background: "linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)",
                backgroundSize: "200% 100%",
                borderRadius: 8,
                animation: "shimmer 1.5s infinite"
              }}></div>
            </div>
          </div>
        </div>

        {/* Table Header Skeleton */}
        <div style={{ padding: "12px 16px", backgroundColor: "#f8fafc", borderBottom: "1px solid #e2e8f0" }}>
          <div className="row">
            {["Car", "Model / Type", "Availability", "Maintenance", "Price/Day", "Actions"].map((_, i) => (
              <div key={i} className="col" style={{ padding: "0 8px" }}>
                <div style={{
                  width: i === 0 ? 80 : i === 1 ? 100 : i === 2 ? 80 : i === 3 ? 80 : i === 4 ? 60 : 50,
                  height: 14,
                  background: "linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)",
                  backgroundSize: "200% 100%",
                  borderRadius: 4,
                  animation: "shimmer 1.5s infinite"
                }}></div>
              </div>
            ))}
          </div>
        </div>

        {/* Table Rows Skeleton */}
        {[...Array(8)].map((_, rowIndex) => (
          <div
            key={rowIndex}
            style={{
              padding: "14px 16px",
              borderBottom: rowIndex < 7 ? "1px solid #f1f5f9" : "none",
              display: "flex",
              alignItems: "center"
            }}
          >
            {[...Array(6)].map((_, colIndex) => (
              <div key={colIndex} style={{ flex: 1, padding: "0 8px" }}>
                <div style={{
                  width: colIndex === 0 ? 140 : colIndex === 1 ? 100 : colIndex === 2 ? 80 : colIndex === 3 ? 80 : colIndex === 4 ? 60 : 70,
                  height: colIndex === 0 ? 16 : 14,
                  background: "linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)",
                  backgroundSize: "200% 100%",
                  borderRadius: 4,
                  animation: "shimmer 1.5s infinite"
                }}></div>
                {colIndex === 0 && (
                  <div style={{
                    width: 80,
                    height: 12,
                    marginTop: 4,
                    background: "linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)",
                    backgroundSize: "200% 100%",
                    borderRadius: 3,
                    animation: "shimmer 1.5s infinite"
                  }}></div>
                )}
              </div>
            ))}
          </div>
        ))}

        {/* Pagination Skeleton */}
        <div style={{
          padding: "12px 20px",
          borderTop: "1px solid #edf2f7",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center"
        }}>
          <div style={{
            width: 200,
            height: 16,
            background: "linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)",
            backgroundSize: "200% 100%",
            borderRadius: 4,
            animation: "shimmer 1.5s infinite"
          }}></div>
          <div style={{ display: "flex", gap: "6px" }}>
            {[...Array(5)].map((_, i) => (
              <div key={i} style={{
                width: i === 0 || i === 4 ? 70 : 32,
                height: 32,
                background: "linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)",
                backgroundSize: "200% 100%",
                borderRadius: 6,
                animation: "shimmer 1.5s infinite"
              }}></div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

const ListCars = () => {
  const navigate = useNavigate();

  const [cars, setCars] = useState<Car[]>([]);
  const [filteredCars, setFilteredCars] = useState<Car[]>([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);

  const [searchTerm, setSearchTerm] = useState("");
  const [availabilityFilter, setAvailabilityFilter] = useState<string>("all");
  const [maintenanceFilter, setMaintenanceFilter] = useState<string>("all");

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedCarToDelete, setSelectedCarToDelete] = useState<Car | null>(null);

  const [showMaintenanceModal, setShowMaintenanceModal] = useState(false);
  const [selectedCarForMaint, setSelectedCarForMaint] = useState<Car | null>(null);
  const [maintenanceDays, setMaintenanceDays] = useState("");
  const [actionLoading, setActionLoading] = useState(false);

  const token = localStorage.getItem("token_admin_silverjarz");

  const config = {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };

  const fetchCars = async (page = 1) => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/cars?page=${page}`, config);
      setCars(response.data.data);
      setFilteredCars(response.data.data);
      setCurrentPage(response.data.current_page);
      setLastPage(response.data.last_page);
    } catch (error) {
      console.error("Error fetching cars:", error);
      toast.error("Error fetching cars");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCars(1);
  }, []);

  useEffect(() => {
    let filtered = cars;

    if (searchTerm) {
      filtered = filtered.filter(
        (car) =>
          car.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          car.model.toLowerCase().includes(searchTerm.toLowerCase()) ||
          car.type.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (availabilityFilter !== "all") {
      const isAvailable = availabilityFilter === "available";
      filtered = filtered.filter((car) => car.is_available_for_rent === isAvailable);
    }

    if (maintenanceFilter !== "all") {
      const inMaintenance = maintenanceFilter === "in_maintenance";
      filtered = filtered.filter((car) => car.is_in_maintenance === inMaintenance);
    }

    setFilteredCars(filtered);
  }, [cars, searchTerm, availabilityFilter, maintenanceFilter]);

  const handleDeleteClick = (car: Car) => {
    setSelectedCarToDelete(car);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!selectedCarToDelete) return;
    try {
      await axios.delete(`${API_URL}/car/${selectedCarToDelete.id}`, config);
      setCars((prev) => prev.filter((c) => c.id !== selectedCarToDelete.id));
      toast.success("Car deleted successfully");
    } catch (error: any) {
      console.error("Error deleting car:", error.response || error.message);
      toast.error("Error deleting car");
    } finally {
      setShowDeleteModal(false);
      setSelectedCarToDelete(null);
    }
  };

  const handleToggleAvailability = async (car: Car) => {
    try {
      setActionLoading(true);
      const response = await axios.put(`${API_URL}/car-availability/${car.id}`, {
        is_available_for_rent: !car.is_available_for_rent,
      }, config);

      if (response.data.status) {
        toast.success(`Car ${!car.is_available_for_rent ? "available" : "unavailable"} successfully`);
        fetchCars(currentPage);
      }
    } catch (error: any) {
      console.error("Error updating car availability:", error.response?.data || error.message);
      toast.error("Error updating car availability");
    } finally {
      setActionLoading(false);
    }
  };

  const handleOpenMaintenanceModal = (car: Car) => {
    setSelectedCarForMaint(car);
    setMaintenanceDays(car.maintenance_days_estimate?.toString() || "1");
    setShowMaintenanceModal(true);
  };

  const handleCloseMaintenanceModal = () => {
    setShowMaintenanceModal(false);
    setSelectedCarForMaint(null);
    setMaintenanceDays("");
  };

  const handleSetMaintenance = async () => {
    if (!selectedCarForMaint) return;
    try {
      setActionLoading(true);
      const response = await axios.put(`${API_URL}/car-maintenance/${selectedCarForMaint.id}`, {
        is_in_maintenance: true,
        maintenance_days_estimate: parseInt(maintenanceDays),
      }, config);

      if (response.data.status) {
        toast.success("Car set to maintenance successfully");
        fetchCars(currentPage);
        handleCloseMaintenanceModal();
      }
    } catch (error: any) {
      console.error("Error setting car to maintenance:", error.response?.data || error.message);
      toast.error("Error setting car to maintenance");
    } finally {
      setActionLoading(false);
    }
  };

  const handleRemoveMaintenance = async (car: Car) => {
    try {
      setActionLoading(true);
      const response = await axios.put(`${API_URL}/car/${car.id}`, {
        is_in_maintenance: false,
        maintenance_days_estimate: null,
      }, config);

      if (response.data.status) {
        toast.success("Car removed from maintenance successfully");
        fetchCars(currentPage);
      }
    } catch (error: any) {
      console.error("Error removing car from maintenance:", error.response?.data || error.message);
      toast.error("Error removing car from maintenance");
    } finally {
      setActionLoading(false);
    }
  };

  const clearFilters = () => {
    setSearchTerm("");
    setAvailabilityFilter("all");
    setMaintenanceFilter("all");
  };

  const hasActiveFilters = searchTerm !== "" || availabilityFilter !== "all" || maintenanceFilter !== "all";

  const getPaginationRange = (current: number, total: number) => {
    const delta = 1;
    const range: (number | string)[] = [];
    for (let i = 1; i <= total; i++) {
      if (i === 1 || i === total || (i >= current - delta && i <= current + delta)) {
        range.push(i);
      } else if (range[range.length - 1] !== "...") {
        range.push("...");
      }
    }
    return range;
  };

  if (loading) {
    return <SkeletonLoader />;
  }

  return (
    <div
      style={{
        width: "100%",
        minHeight: "100vh",
        backgroundColor: "#f4f6f8",
        padding: "20px 20px",
        boxSizing: "border-box",
        color: "#1e293b",
      }}
    >
      <ToastContainer position="top-right" autoClose={3000} />

      {/* SECTION EN-TÊTE */}
      <div className="d-flex justify-content-between align-items-end mb-4">
        <div>
          <div className="d-flex align-items-center gap-2 mb-1">
            <h1
              style={{
                color: "#294b5a",
                fontSize: "22px",
                fontWeight: 700,
                margin: 0,
                letterSpacing: "-0.02em",
              }}
            >
              Manage Cars
            </h1>
            <span
              style={{
                backgroundColor: "#294b5a",
                color: "#d3be76",
                fontSize: "11px",
                fontWeight: 700,
                padding: "2px 8px",
                borderRadius: "12px",
                letterSpacing: "0.5px",
              }}
            >
              System
            </span>
          </div>
          <p
            style={{
              color: "#64748b",
              fontSize: "13.5px",
              margin: 0,
              fontWeight: 400,
            }}
          >
            <strong style={{ color: "#294b5a" }}>{cars.length}</strong> total cars
            {hasActiveFilters && ` • ${filteredCars.length} filtered`}
          </p>
        </div>

        <button
          onClick={() => navigate("/admin/add-car")}
          style={{
            backgroundColor: "#294b5a",
            color: "#ffffff",
            border: "none",
            borderRadius: "8px",
            padding: "10px 18px",
            fontSize: "13.5px",
            fontWeight: 600,
            display: "inline-flex",
            alignItems: "center",
            gap: "8px",
            cursor: "pointer",
            transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
            boxShadow: "0 2px 8px rgba(41, 75, 90, 0.25)",
          }}
          onMouseOver={(e) => {
            e.currentTarget.style.backgroundColor = "#1e3743";
            e.currentTarget.style.boxShadow = "0 4px 12px rgba(41, 75, 90, 0.35)";
          }}
          onMouseOut={(e) => {
            e.currentTarget.style.backgroundColor = "#294b5a";
            e.currentTarget.style.boxShadow = "0 2px 8px rgba(41, 75, 90, 0.25)";
          }}
        >
          <i className="fa-solid fa-plus" style={{ color: "#fff", fontSize: "13px" }}></i>
          <span>Add New Car</span>
        </button>
      </div>

      {/* BLOC PRINCIPAL */}
      <div
        style={{
          backgroundColor: "#ffffff",
          borderRadius: "12px",
          border: "1px solid #e2e8f0",
          boxShadow: "0 4px 16px -2px rgba(0,0,0,0.03)",
          overflow: "hidden",
          marginTop: "15px",
        }}
      >
        {/* BARRE DE FILTRES */}
        <div
          style={{
            padding: "16px 20px",
            borderBottom: "1px solid #edf2f7",
            backgroundColor: "#ffffff",
          }}
        >
          <div className="row g-3 align-items-center">
            {/* Search */}
            <div className="col-12 col-md-6 col-lg-5">
              <div className="position-relative">
                <input
                  type="text"
                  placeholder="Search by name, model or type..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "9px 36px 9px 38px",
                    border: "1px solid #cbd5e1",
                    borderRadius: "8px",
                    fontSize: "13px",
                    outline: "none",
                    color: "#0f172a",
                    backgroundColor: "#f8fafc",
                    transition: "all 0.2s ease",
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = "#294b5a";
                    e.target.style.backgroundColor = "#ffffff";
                    e.target.style.boxShadow = "0 0 0 3px rgba(41, 75, 90, 0.1)";
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = "#cbd5e1";
                    e.target.style.backgroundColor = "#f8fafc";
                    e.target.style.boxShadow = "none";
                  }}
                />
                <i
                  className="fa-solid fa-magnifying-glass"
                  style={{
                    position: "absolute",
                    left: "14px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    fontSize: "13px",
                    color: "#94a3b8",
                  }}
                ></i>
                {searchTerm && (
                  <button
                    type="button"
                    onClick={() => setSearchTerm("")}
                    style={{
                      position: "absolute",
                      right: "12px",
                      top: "50%",
                      transform: "translateY(-50%)",
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      color: "#94a3b8",
                      padding: "0",
                    }}
                  >
                    <i className="fa-solid fa-xmark" style={{ fontSize: "14px" }}></i>
                  </button>
                )}
              </div>
            </div>

            {/* Availability Filter */}
            <div className="col-12 col-md-4 col-lg-3">
              <select
                value={availabilityFilter}
                onChange={(e) => setAvailabilityFilter(e.target.value)}
                style={{
                  width: "100%",
                  padding: "9px 12px",
                  border: "1px solid #cbd5e1",
                  borderRadius: "8px",
                  fontSize: "13px",
                  color: "#334155",
                  outline: "none",
                  backgroundColor: "#f8fafc",
                  cursor: "pointer",
                  transition: "all 0.2s ease",
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = "#294b5a";
                  e.target.style.backgroundColor = "#ffffff";
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = "#cbd5e1";
                  e.target.style.backgroundColor = "#f8fafc";
                }}
              >
                <option value="all">All Availability</option>
                <option value="available">Available</option>
                <option value="unavailable">Unavailable</option>
              </select>
            </div>

            {/* Maintenance Filter */}
            <div className="col-12 col-md-4 col-lg-3">
              <select
                value={maintenanceFilter}
                onChange={(e) => setMaintenanceFilter(e.target.value)}
                style={{
                  width: "100%",
                  padding: "9px 12px",
                  border: "1px solid #cbd5e1",
                  borderRadius: "8px",
                  fontSize: "13px",
                  color: "#334155",
                  outline: "none",
                  backgroundColor: "#f8fafc",
                  cursor: "pointer",
                  transition: "all 0.2s ease",
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = "#294b5a";
                  e.target.style.backgroundColor = "#ffffff";
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = "#cbd5e1";
                  e.target.style.backgroundColor = "#f8fafc";
                }}
              >
                <option value="all">All Maintenance</option>
                <option value="in_maintenance">In Maintenance</option>
                <option value="not_in_maintenance">Not in Maintenance</option>
              </select>
            </div>

            {hasActiveFilters && (
              <div className="col-12 col-md-12 col-lg-1">
                <button
                  type="button"
                  onClick={clearFilters}
                  style={{
                    background: "none",
                    border: "none",
                    color: "#294b5a",
                    cursor: "pointer",
                    fontSize: "13px",
                    fontWeight: 600,
                    textDecoration: "underline",
                    padding: "9px 0",
                  }}
                >
                  Clear Filters
                </button>
              </div>
            )}
          </div>
        </div>

        {/* TABLEAU */}
        {filteredCars.length === 0 ? (
          <div style={{ padding: "48px 20px", textAlign: "center" }}>
            <i className="fa-solid fa-car" style={{ fontSize: "24px", color: "#cbd5e1", display: "block", marginBottom: "8px" }}></i>
            <div style={{ fontSize: "13.5px", color: "#64748b" }}>
              {hasActiveFilters ? "No cars match your filters." : "No cars found."}
            </div>
            {hasActiveFilters && (
              <button
                type="button"
                onClick={clearFilters}
                style={{
                  background: "none",
                  border: "none",
                  color: "#294b5a",
                  cursor: "pointer",
                  fontSize: "13px",
                  fontWeight: 600,
                  textDecoration: "underline",
                  marginTop: "8px",
                }}
              >
                Clear all filters
              </button>
            )}
          </div>
        ) : (
          <>
            <div className="table-responsive" style={{ margin: 0 }}>
              <table style={{ width: "100%", borderCollapse: "collapse", textAlign: "left" }}>
                <thead>
                  <tr style={{ backgroundColor: "#f8fafc", borderBottom: "1px solid #e2e8f0" }}>
                    <th style={{ padding: "12px 16px", color: "#294b5a", fontSize: "12px", fontWeight: 700, letterSpacing: "0.5px" }}>Car</th>
                    <th style={{ padding: "12px 16px", color: "#294b5a", fontSize: "12px", fontWeight: 700, letterSpacing: "0.5px" }}>Model / Type</th>
                    <th style={{ padding: "12px 16px", color: "#294b5a", fontSize: "12px", fontWeight: 700, letterSpacing: "0.5px" }}>Availability</th>
                    <th style={{ padding: "12px 16px", color: "#294b5a", fontSize: "12px", fontWeight: 700, letterSpacing: "0.5px" }}>Maintenance</th>
                    <th style={{ padding: "12px 16px", color: "#294b5a", fontSize: "12px", fontWeight: 700, letterSpacing: "0.5px" }}>Price/Day</th>
                    <th style={{ padding: "12px 16px", color: "#294b5a", fontSize: "12px", fontWeight: 700, letterSpacing: "0.5px", textAlign: "right" }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredCars.map((item) => {
                    const photo = item.photos?.[0];
                    return (
                      <tr
                        key={item.id}
                        style={{ borderBottom: "1px solid #f1f5f9", transition: "background-color 0.15s ease" }}
                        onMouseOver={(e) => (e.currentTarget.style.backgroundColor = "#fafaf8")}
                        onMouseOut={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
                      >
                        <td style={{ padding: "14px 16px" }}>
                          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                            <div
                              style={{
                                width: 48,
                                height: 48,
                                borderRadius: "8px",
                                overflow: "hidden",
                                flexShrink: 0,
                                backgroundColor: "#f8fafc",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                color: "#94a3b8",
                              }}
                            >
                              {photo ? (
                                <img
                                  src={`${Back_url}/${photo.path}`}
                                  alt={item.name}
                                  style={{ width: "100%", height: "100%", objectFit: "cover" }}
                                />
                              ) : (
                                <i className="fa-solid fa-image" style={{ fontSize: "20px" }}></i>
                              )}
                            </div>
                            <span style={{ fontSize: "13.5px", fontWeight: 600, color: "#0f172a" }}>{item.name}</span>
                          </div>
                        </td>
                        <td style={{ padding: "14px 16px" }}>
                          <div style={{ fontSize: "13.5px", color: "#475569" }}>{item.model}</div>
                          <div style={{ fontSize: "12px", color: "#94a3b8" }}>{item.type}</div>
                        </td>
                        <td style={{ padding: "14px 16px" }}>
                          <div style={{ display: "flex", flexDirection: "column", gap: "6px", alignItems: "flex-start" }}>
                            {item.is_available_for_rent ? (
                              <span style={badgeStyle("#d1fae5", "#065f46")}>Available</span>
                            ) : (
                              <span style={badgeStyle("#fee2e2", "#991b1b")}>Unavailable</span>
                            )}
                            <button
                              type="button"
                              disabled={actionLoading}
                              onClick={() => handleToggleAvailability(item)}
                              style={togglePillStyle}
                            >
                              Toggle
                            </button>
                          </div>
                        </td>
                        <td style={{ padding: "14px 16px" }}>
                          <div style={{ display: "flex", flexDirection: "column", gap: "6px", alignItems: "flex-start" }}>
                            {item.is_in_maintenance ? (
                              <>
                                <span style={badgeStyle("#fef3c7", "#92400e")}>
                                  Maint. ({item.maintenance_days_estimate}d)
                                </span>
                                <button
                                  type="button"
                                  disabled={actionLoading}
                                  onClick={() => handleRemoveMaintenance(item)}
                                  style={togglePillStyle}
                                >
                                  Remove
                                </button>
                              </>
                            ) : (
                              <button
                                type="button"
                                disabled={actionLoading}
                                onClick={() => handleOpenMaintenanceModal(item)}
                                style={togglePillStyle}
                              >
                                Set Maint.
                              </button>
                            )}
                          </div>
                        </td>
                        <td style={{ padding: "14px 16px", fontSize: "13.5px", fontWeight: 600, color: "#294b5a", whiteSpace: "nowrap" }}>
                          ${item.price_per_day}
                        </td>
                        <td style={{ padding: "14px 16px", textAlign: "right" }}>
                          <div style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}>
                            <button
                              type="button"
                              onClick={() => navigate(`/admin/edit-car/${item.id}`)}
                              style={iconButtonStyle()}
                              onMouseOver={(e) => {
                                e.currentTarget.style.backgroundColor = "#f8fafc";
                                e.currentTarget.style.borderColor = "#cbd5e1";
                                e.currentTarget.style.color = "#d3be76";
                              }}
                              onMouseOut={(e) => {
                                e.currentTarget.style.backgroundColor = "#ffffff";
                                e.currentTarget.style.borderColor = "#e2e8f0";
                                e.currentTarget.style.color = "#64748b";
                              }}
                              title="Edit"
                            >
                              <i className="fa-solid fa-pen-to-square" style={{ fontSize: "13px" }}></i>
                            </button>
                            <button
                              type="button"
                              onClick={() => handleDeleteClick(item)}
                              style={iconButtonStyle(true)}
                              onMouseOver={(e) => {
                                e.currentTarget.style.backgroundColor = "#fef2f2";
                                e.currentTarget.style.borderColor = "#fecaca";
                                e.currentTarget.style.color = "#dc2626";
                              }}
                              onMouseOut={(e) => {
                                e.currentTarget.style.backgroundColor = "#ffffff";
                                e.currentTarget.style.borderColor = "#e2e8f0";
                                e.currentTarget.style.color = "#64748b";
                              }}
                              title="Delete"
                            >
                              <i className="fa-solid fa-trash-can" style={{ fontSize: "13px" }}></i>
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* PAGINATION */}
            {cars.length > 0 && lastPage > 1 && (
              <div
                style={{
                  padding: "12px 20px",
                  borderTop: "1px solid #edf2f7",
                  backgroundColor: "#ffffff",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  flexWrap: "wrap",
                  gap: "10px",
                }}
              >
                <div style={{ fontSize: "13px", color: "#64748b" }}>
                  Page <strong>{currentPage}</strong> of <strong>{lastPage}</strong>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                  <button
                    onClick={() => fetchCars(currentPage - 1)}
                    disabled={currentPage === 1}
                    style={{
                      padding: "6px 12px",
                      fontSize: "12.5px",
                      fontWeight: "600",
                      border: "1px solid #cbd5e1",
                      borderRadius: "6px",
                      backgroundColor: "#ffffff",
                      color: currentPage === 1 ? "#94a3b8" : "#294b5a",
                      cursor: currentPage === 1 ? "not-allowed" : "pointer",
                      transition: "all 0.15s ease",
                    }}
                  >
                    Previous
                  </button>

                  {getPaginationRange(currentPage, lastPage).map((item, index) => {
                    if (item === "...") {
                      return (
                        <span
                          key={`dots-${index}`}
                          style={{ padding: "0 6px", fontSize: "13px", color: "#94a3b8", fontWeight: "600", userSelect: "none" }}
                        >
                          ...
                        </span>
                      );
                    }
                    const pageNum = item as number;
                    const isActive = currentPage === pageNum;
                    return (
                      <button
                        key={pageNum}
                        onClick={() => fetchCars(pageNum)}
                        style={{
                          width: "32px",
                          height: "32px",
                          fontSize: "12.5px",
                          fontWeight: "600",
                          borderRadius: "6px",
                          border: isActive ? "none" : "1px solid #cbd5e1",
                          backgroundColor: isActive ? "#294b5a" : "#ffffff",
                          color: isActive ? "#ffffff" : "#334155",
                          cursor: "pointer",
                          transition: "all 0.15s ease",
                        }}
                      >
                        {pageNum}
                      </button>
                    );
                  })}

                  <button
                    onClick={() => fetchCars(currentPage + 1)}
                    disabled={currentPage === lastPage}
                    style={{
                      padding: "6px 12px",
                      fontSize: "12.5px",
                      fontWeight: "600",
                      border: "1px solid #cbd5e1",
                      borderRadius: "6px",
                      backgroundColor: "#ffffff",
                      color: currentPage === lastPage ? "#94a3b8" : "#294b5a",
                      cursor: currentPage === lastPage ? "not-allowed" : "pointer",
                      transition: "all 0.15s ease",
                    }}
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Modal: Confirmation de suppression */}
      {showDeleteModal && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(15, 23, 42, 0.55)",
            backdropFilter: "blur(4px)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1050,
            padding: "16px",
          }}
        >
          <div
            style={{
              backgroundColor: "#ffffff",
              borderRadius: "16px",
              width: "100%",
              maxWidth: "440px",
              boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)",
              overflow: "hidden",
              border: "1px solid #e2e8f0",
            }}
          >
            <div style={{ padding: "28px 24px 20px 24px", textAlign: "center" }}>
              <div
                style={{
                  width: "56px",
                  height: "56px",
                  borderRadius: "50%",
                  backgroundColor: "#fef2f2",
                  border: "1px solid #fecaca",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  marginBottom: "16px",
                }}
              >
                <i className="fa-solid fa-trash-can" style={{ color: "#dc2626", fontSize: "22px" }}></i>
              </div>

              <h2 style={{ fontSize: "18px", fontWeight: 700, color: "#294b5a", margin: "0 0 8px 0", letterSpacing: "-0.01em" }}>
                Delete Car?
              </h2>

              <p style={{ fontSize: "13.5px", color: "#64748b", margin: 0, lineHeight: "1.5" }}>
                Are you sure you want to delete <strong>{selectedCarToDelete?.name}</strong>? This action cannot be undone.
              </p>

              {selectedCarToDelete && (
                <div
                  style={{
                    marginTop: "16px",
                    padding: "12px 16px",
                    backgroundColor: "#f8fafc",
                    borderRadius: "10px",
                    border: "1px solid #e2e8f0",
                    textAlign: "left",
                  }}
                >
                  <div style={{ fontSize: "13.5px", fontWeight: 700, color: "#0f172a" }}>
                    {selectedCarToDelete.name}
                  </div>
                  <div style={{ fontSize: "12px", color: "#64748b" }}>
                    {selectedCarToDelete.model} · {selectedCarToDelete.type}
                  </div>
                </div>
              )}
            </div>

            <div style={{ display: "flex", alignItems: "center", gap: "12px", padding: "16px 24px 20px 24px", backgroundColor: "#ffffff" }}>
              <button
                type="button"
                onClick={() => setShowDeleteModal(false)}
                style={{
                  flex: 1,
                  padding: "10px 16px",
                  fontSize: "13.5px",
                  fontWeight: "600",
                  color: "#64748b",
                  backgroundColor: "#ffffff",
                  border: "1px solid #cbd5e1",
                  borderRadius: "8px",
                  cursor: "pointer",
                  transition: "all 0.15s ease",
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.backgroundColor = "#f8fafc";
                  e.currentTarget.style.color = "#0f172a";
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.backgroundColor = "#ffffff";
                  e.currentTarget.style.color = "#64748b";
                }}
              >
                Cancel
              </button>

              <button
                type="button"
                onClick={handleConfirmDelete}
                style={{
                  flex: 1,
                  padding: "10px 16px",
                  fontSize: "13.5px",
                  fontWeight: "600",
                  color: "#ffffff",
                  backgroundColor: "#dc2626",
                  border: "none",
                  borderRadius: "8px",
                  cursor: "pointer",
                  boxShadow: "0 2px 6px rgba(220, 38, 38, 0.25)",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "8px",
                  transition: "all 0.15s ease",
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.backgroundColor = "#b91c1c";
                  e.currentTarget.style.boxShadow = "0 4px 10px rgba(220, 38, 38, 0.35)";
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.backgroundColor = "#dc2626";
                  e.currentTarget.style.boxShadow = "0 2px 6px rgba(220, 38, 38, 0.25)";
                }}
              >
                <i className="fa-solid fa-trash-can" style={{ fontSize: "13px" }}></i>
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal: Maintenance */}
      {showMaintenanceModal && selectedCarForMaint && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(15, 23, 42, 0.55)",
            backdropFilter: "blur(4px)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1050,
            padding: "16px",
          }}
        >
          <div
            style={{
              backgroundColor: "#ffffff",
              borderRadius: "16px",
              width: "100%",
              maxWidth: "440px",
              boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)",
              overflow: "hidden",
              border: "1px solid #e2e8f0",
            }}
          >
            <div style={{ padding: "28px 24px 20px 24px", textAlign: "center" }}>
              <div
                style={{
                  width: "56px",
                  height: "56px",
                  borderRadius: "50%",
                  backgroundColor: "#fef3c7",
                  border: "1px solid #fde68a",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  marginBottom: "16px",
                }}
              >
                <i className="fa-solid fa-wrench" style={{ color: "#92400e", fontSize: "22px" }}></i>
              </div>

              <h2 style={{ fontSize: "18px", fontWeight: 700, color: "#294b5a", margin: "0 0 8px 0", letterSpacing: "-0.01em" }}>
                Set Car Maintenance
              </h2>

              <p style={{ fontSize: "13.5px", color: "#64748b", margin: 0, lineHeight: "1.5" }}>
                Set maintenance for <strong>{selectedCarForMaint.name}</strong>
              </p>
            </div>

            <div style={{ padding: "0 24px 20px 24px" }}>
              <label style={{ fontSize: "13px", fontWeight: 600, color: "#294b5a", display: "block", marginBottom: "6px" }}>
                Maintenance Days
              </label>
              <input
                type="number"
                min="1"
                max="365"
                value={maintenanceDays}
                onChange={(e) => setMaintenanceDays(e.target.value)}
                style={{
                  width: "100%",
                  padding: "9px 12px",
                  border: "1px solid #cbd5e1",
                  borderRadius: "8px",
                  fontSize: "13px",
                  outline: "none",
                  color: "#0f172a",
                  backgroundColor: "#f8fafc",
                  boxSizing: "border-box",
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = "#294b5a";
                  e.target.style.backgroundColor = "#ffffff";
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = "#cbd5e1";
                  e.target.style.backgroundColor = "#f8fafc";
                }}
              />
            </div>

            <div style={{ display: "flex", alignItems: "center", gap: "12px", padding: "16px 24px 20px 24px", backgroundColor: "#ffffff", borderTop: "1px solid #edf2f7" }}>
              <button
                type="button"
                onClick={handleCloseMaintenanceModal}
                style={{
                  flex: 1,
                  padding: "10px 16px",
                  fontSize: "13.5px",
                  fontWeight: "600",
                  color: "#64748b",
                  backgroundColor: "#ffffff",
                  border: "1px solid #cbd5e1",
                  borderRadius: "8px",
                  cursor: "pointer",
                  transition: "all 0.15s ease",
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.backgroundColor = "#f8fafc";
                  e.currentTarget.style.color = "#0f172a";
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.backgroundColor = "#ffffff";
                  e.currentTarget.style.color = "#64748b";
                }}
              >
                Cancel
              </button>

              <button
                type="button"
                onClick={handleSetMaintenance}
                disabled={actionLoading || !maintenanceDays}
                style={{
                  flex: 1,
                  padding: "10px 16px",
                  fontSize: "13.5px",
                  fontWeight: "600",
                  color: "#ffffff",
                  backgroundColor: actionLoading || !maintenanceDays ? "#94a3b8" : "#294b5a",
                  border: "none",
                  borderRadius: "8px",
                  cursor: actionLoading || !maintenanceDays ? "not-allowed" : "pointer",
                  boxShadow: actionLoading || !maintenanceDays ? "none" : "0 2px 8px rgba(41, 75, 90, 0.25)",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "8px",
                  transition: "all 0.15s ease",
                }}
                onMouseOver={(e) => {
                  if (!actionLoading && maintenanceDays) {
                    e.currentTarget.style.backgroundColor = "#1e3743";
                    e.currentTarget.style.boxShadow = "0 4px 12px rgba(41, 75, 90, 0.35)";
                  }
                }}
                onMouseOut={(e) => {
                  if (!actionLoading && maintenanceDays) {
                    e.currentTarget.style.backgroundColor = "#294b5a";
                    e.currentTarget.style.boxShadow = "0 2px 8px rgba(41, 75, 90, 0.25)";
                  }
                }}
              >
                <i className="fa-solid fa-wrench" style={{ fontSize: "13px" }}></i>
                {actionLoading ? "Setting..." : "Confirm"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ListCars;